import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserPushCampaign = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, delivered: 0, read: 0 });
  const [loading, setLoading] = useState(true);
  const [campaignData, setCampaignData] = useState({
    name: '', template_id: '', audience: 'upload', audienceFile: null, scheduledAt: '', mediaType: 'none', mediaUrl: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [campRes, tplRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/campaigns`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/templates?status=APPROVED`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/campaigns/stats`, { headers }).catch(() => null),
      ]);
      if (campRes?.ok) { const d = await campRes.json(); setCampaigns(d.campaigns || []); }
      if (tplRes?.ok) { const d = await tplRes.json(); setTemplates(d.templates || []); }
      if (statsRes?.ok) { const d = await statsRes.json(); setStats(d || { total: 0, sent: 0, delivered: 0, read: 0 }); }
    } catch (err) {
      console.error('Failed to fetch campaign data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColors = {
    draft: { bg: '#f3f4f6', text: '#374151' },
    scheduled: { bg: '#dbeafe', text: '#1e40af' },
    queued: { bg: '#fef3c7', text: '#92400e' },
    running: { bg: '#fef3c7', text: '#92400e' },
    completed: { bg: '#dcfce7', text: '#166534' },
    failed: { bg: '#fee2e2', text: '#991b1b' },
    cancelled: { bg: '#f3f4f6', text: '#374151' },
    paused: { bg: '#f3e8ff', text: '#6b21a8' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.Megaphone className="w-5 h-5 text-white" />
            </div>
            Push Campaign
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage WhatsApp broadcast campaigns</p>
        </div>
        <button
          onClick={() => setActiveTab('create')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm transition-all hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
        >
          <Icons.Plus className="w-4 h-4" />
          New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Campaigns', value: stats.total || campaigns.length || '0', icon: 'Megaphone', color: '#25D366', bg: 'rgba(37, 211, 102, 0.08)' },
          { label: 'Messages Sent', value: (stats.sent || 0).toLocaleString(), icon: 'Send', color: '#128C7E', bg: 'rgba(18, 140, 126, 0.08)' },
          { label: 'Delivered', value: (stats.delivered || 0).toLocaleString(), icon: 'CheckCheck', color: '#34B7F1', bg: 'rgba(52, 183, 241, 0.08)' },
          { label: 'Read Rate', value: stats.sent > 0 ? `${((stats.read / stats.sent) * 100).toFixed(1)}%` : '0%', icon: 'Eye', color: '#075E54', bg: 'rgba(7, 94, 84, 0.08)' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: stat.bg, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { key: 'campaigns', label: 'All Campaigns', icon: 'List' },
            { key: 'create', label: 'Create Campaign', icon: 'Plus' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'campaigns' && (
            <div>
              {loading ? (
                <div className="text-center py-12">
                  <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-gray-500">Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                    <Icons.Megaphone className="w-8 h-8" style={{ color: '#25D366' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No Campaigns Yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Create your first WhatsApp push campaign to start reaching your audience.</p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium"
                    style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                  >
                    <Icons.Plus className="w-4 h-4" />
                    Create Campaign
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                          <Icons.Megaphone className="w-5 h-5" style={{ color: '#25D366' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{campaign.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {campaign.scheduled_at ? `Scheduled: ${campaign.scheduled_at}` : `Created: ${campaign.created_at}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden md:block">
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Sent: <strong className="text-gray-700">{(campaign.total_sent || 0).toLocaleString()}</strong></span>
                            <span>Delivered: <strong className="text-green-600">{(campaign.total_delivered || 0).toLocaleString()}</strong></span>
                            <span>Read: <strong className="text-blue-600">{(campaign.total_read || 0).toLocaleString()}</strong></span>
                            <span>Failed: <strong className="text-red-600">{(campaign.total_failed || 0).toLocaleString()}</strong></span>
                          </div>
                        </div>
                        <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                          background: statusColors[campaign.status]?.bg || '#f3f4f6',
                          color: statusColors[campaign.status]?.text || '#374151',
                        }}>
                          {(campaign.status || 'draft').toUpperCase()}
                        </span>
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                            <Icons.Eye className="w-4 h-4" />
                          </button>
                          <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                            <Icons.Copy className="w-4 h-4" />
                          </button>
                          {campaign.status === 'draft' && (
                            <button className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500">
                              <Icons.Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'create' && (
            <div className="max-w-3xl space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                    placeholder="Enter campaign name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message Template</label>
                  <select
                    value={campaignData.template_id}
                    onChange={(e) => setCampaignData({ ...campaignData, template_id: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  >
                    <option value="">Select template...</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.category} - {tpl.language})</option>
                    ))}
                  </select>
                  {templates.length === 0 && !loading && (
                    <p className="text-[10px] text-yellow-600 mt-1">No approved templates found. Create and get templates approved first.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Audience</label>
                <div className="flex gap-3">
                  {['all', 'segment', 'upload'].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setCampaignData({ ...campaignData, audience: opt })}
                      className="px-4 py-2 rounded-xl text-sm font-medium border transition-all"
                      style={{
                        borderColor: campaignData.audience === opt ? '#25D366' : '#e5e7eb',
                        background: campaignData.audience === opt ? 'rgba(37, 211, 102, 0.08)' : 'white',
                        color: campaignData.audience === opt ? '#128C7E' : '#6b7280',
                      }}
                    >
                      {opt === 'all' ? 'All Contacts' : opt === 'segment' ? 'Segment' : 'Upload CSV'}
                    </button>
                  ))}
                </div>
              </div>

              {campaignData.audience === 'upload' && (
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-green-300 transition-all">
                  <Icons.Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Drag & drop CSV file or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">Format: phone_number, name, variable1, variable2...</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Media Attachment</label>
                <div className="flex gap-3">
                  {[
                    { key: 'none', label: 'None', icon: 'Ban' },
                    { key: 'image', label: 'Image', icon: 'Image' },
                    { key: 'video', label: 'Video', icon: 'Video' },
                    { key: 'document', label: 'Document', icon: 'FileText' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setCampaignData({ ...campaignData, mediaType: opt.key })}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all"
                      style={{
                        borderColor: campaignData.mediaType === opt.key ? '#25D366' : '#e5e7eb',
                        background: campaignData.mediaType === opt.key ? 'rgba(37, 211, 102, 0.08)' : 'white',
                        color: campaignData.mediaType === opt.key ? '#128C7E' : '#6b7280',
                      }}
                    >
                      {getIcon(opt.icon, 14)}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Schedule</label>
                <input
                  type="datetime-local"
                  value={campaignData.scheduledAt}
                  onChange={(e) => setCampaignData({ ...campaignData, scheduledAt: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty to send immediately</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-medium text-sm hover:shadow-lg transition-all" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                  <Icons.Send className="w-4 h-4" />
                  {campaignData.scheduledAt ? 'Schedule Campaign' : 'Send Now'}
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-gray-600 font-medium text-sm border border-gray-200 hover:bg-gray-50 transition-all">
                  <Icons.Save className="w-4 h-4" />
                  Save as Draft
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPushCampaign;
